import { Box, Divider } from '@mui/material';
import { useParams, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { fetchBookings } from 'src/features/bookings/api/lib/bookings';
import { bookingsQueryKeys } from 'src/features/bookings/api/query-keys/bookings';
import { BookingStateFilterPopover } from 'src/features/bookings/components/molecules/filters/booking-state-filter-popover';
import { BookingsFilterChips } from 'src/features/bookings/components/molecules/filters/bookings-filter-chips';
import { BookingsMoreFilters } from 'src/features/bookings/components/organisms/bookings-more-filters';
import { fetchEmployees } from 'src/features/employees/api/lib/employees';
import { employeesKeys } from 'src/features/employees/api/query-keys/employees';
import { useMeilisearch } from 'src/hooks/meilisearch/use-meilisearch';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useSortController } from 'src/hooks/use-sort-controller';
import { withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { BookingsTable } from '../../organisms/employees-group-details/bookings/bookings-table';

const EmployeesGroupDetailsBookings = () => {
  const { t } = useTranslation(['employeesGroups', 'common']);

  const { directoryToken } = useUserStore();

  const { query, perPage, page, filter } = useSearch({
    from: '/_layout/tenant/employees/groups/$groupToken/_layout/bookings',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const { groupToken } = useParams({
    from: '/_layout/tenant/employees/groups/$groupToken/_layout/bookings',
  });

  const { results: employees } = useMeilisearch({
    fetch: fetchEmployees,
    queryKey: employeesKeys.list(directoryToken ?? '', 1, 1000),
    page: 1,
    perPage: 1000,
    searchState: {
      filter: [`employeeGroupToken = ${groupToken}`],
      q: '',
      sort: [],
    },
  });

  const [sortController, setSortController] = useSortController({
    sortBy: 'employeeBookerEmail',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
      filter: {
        employeeBookerToken: {
          value: employees?.hits.map(employee => employee.token) ?? [],
        },
      },
    };
  }, [employees?.hits, sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeFilterValue,
    handleChangeSort,
    resetFilters,
  } = useMeilisearchWithUrlParams(
    fetchBookings,
    bookingsQueryKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
    Boolean(employees?.hits.length),
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  return (
    <>
      <Page.Header
        title={t('employeesGroups:bookings.title')}
        headerVariant="h5"
      />

      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />

          <BookingStateFilterPopover
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
          />

          <BookingsMoreFilters
            anchor="right"
            filters={filter}
            handleChangeFilterValue={handleChangeFilterValue}
            resetFilters={resetFilters}
          />
        </Box>

        <BookingsFilterChips
          filters={filter}
          handleChangeFilterValue={handleChangeFilterValue}
        />

        <BookingsTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'EmployeesGroupBookings'),
  EmployeesGroupDetailsBookings,
);
